import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { useSegments, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import { useResponsive } from '../utils/responsive';
import type { ColorTheme } from '../utils/theme';

export interface NavItem {
  readonly icon: keyof typeof Ionicons.glyphMap;
  readonly label: string;
  readonly route: string;
}

/** NavBanner - responsive navigation; vertical sidebar on desktop, horizontal icon bar on mobile. */
interface NavBannerProps {
  readonly items: readonly NavItem[];
  readonly rolePrefix: string;
  readonly logoVariant?: 'default' | 'admin';
  readonly mainRoutes: readonly string[];
  readonly accountRoute?: string;
  /** Keep the nav visible on sub-pages (mobile) instead of collapsing to just the back header. */
  readonly persistOnSubpages?: boolean;
}

const SIDEBAR_WIDTH = 260;

export default function NavBanner({ items, rolePrefix, logoVariant = 'default', mainRoutes, accountRoute, persistOnSubpages = false }: NavBannerProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const segments = useSegments();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { isDesktopWeb } = useResponsive();

  const roleIndex = segments.indexOf(rolePrefix);
  if (roleIndex < 0) return null;

  const rawSegment = segments[roleIndex + 1] as string | undefined;
  const currentSegment = rawSegment ?? 'index';
  const isMainPage = mainRoutes.includes(currentSegment);
  const hasSubSegments = segments.length > roleIndex + 2;

  // Mobile: by default collapse on sub-pages; persistOnSubpages keeps it shown
  // so deep screens (e.g. the gift/credit/promo flows) keep the nav header.
  if (!isDesktopWeb && !persistOnSubpages && (!isMainPage || hasSubSegments)) return null;

  const isAccountActive = accountRoute ? currentSegment === accountRoute : false;

  const accountIcon = accountRoute ? (
    <TouchableOpacity
      style={styles.accountIconBtn}
      onPress={() => {
        if (!isAccountActive) {
          router.replace(`/${rolePrefix}/${accountRoute}` as never);
        }
      }}
      accessibilityRole="button"
      accessibilityLabel="Account"
    >
      <Ionicons
        name="settings-outline"
        size={24}
        color={isAccountActive ? colors.primary : colors.textLight}
      />
    </TouchableOpacity>
  ) : null;

  const logo = (
    <Text style={isDesktopWeb ? styles.sidebarLogoText : styles.logoText}>
      {logoVariant === 'admin' ? (
        <>
          Rinse<Text style={styles.logoPay}>Pay</Text>
          <Text style={styles.logoAdmin}> Admin</Text>
        </>
      ) : (
        <>
          Rinse<Text style={styles.logoPay}>Pay</Text>
        </>
      )}
    </Text>
  );

  // Desktop web: render as vertical sidebar
  if (isDesktopWeb) {
    return (
      <View style={[styles.sidebar, { paddingTop: insets.top }]}>
        <View style={styles.sidebarLogoRow}>
          {logo}
          {accountIcon}
        </View>
        <ScrollView
          style={styles.sidebarScroll}
          contentContainerStyle={styles.sidebarScrollContent}
          showsVerticalScrollIndicator={false}
        >
          {items.map((item) => {
            const isActive = currentSegment === item.route;
            return (
              <TouchableOpacity
                key={item.route}
                style={[styles.sidebarItem, isActive && styles.sidebarItemActive]}
                onPress={() => {
                  if (!isActive) {
                    const path = item.route === 'index'
                      ? `/${rolePrefix}`
                      : `/${rolePrefix}/${item.route}`;
                    router.replace(path as never);
                  }
                }}
                accessibilityRole="tab"
                accessibilityState={{ selected: isActive }}
                accessibilityLabel={item.label}
              >
                <Ionicons
                  name={item.icon}
                  size={20}
                  color={isActive ? colors.primary : colors.textLight}
                />
                <Text
                  style={[styles.sidebarLabel, isActive && styles.sidebarLabelActive]}
                  numberOfLines={1}
                >
                  {item.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
    );
  }

  // Mobile/tablet: horizontal icon bar
  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.logoRow}>
        {logo}
        {accountIcon}
      </View>
      <View style={styles.iconBar}>
        {items.map((item) => {
          const isActive = currentSegment === item.route;
          return (
            <TouchableOpacity
              key={item.route}
              style={styles.iconButton}
              onPress={() => {
                if (!isActive) {
                  const path = item.route === 'index'
                    ? `/${rolePrefix}`
                    : `/${rolePrefix}/${item.route}`;
                  router.replace(path as never);
                }
              }}
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
              accessibilityLabel={item.label}
            >
              <Ionicons
                name={item.icon}
                size={items.length > 6 ? 22 : 26}
                color={isActive ? colors.primary : colors.textLight}
              />
              <Text
                style={[
                  styles.iconLabel,
                  items.length > 6 && styles.iconLabelSmall,
                  isActive && styles.iconLabelActive,
                ]}
                numberOfLines={1}
              >
                {item.label}
              </Text>
              {isActive && <View style={styles.activeIndicator} />}
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  // Mobile icon bar styles
  container: {
    backgroundColor: c.surface,
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  logoText: {
    fontSize: 28,
    fontWeight: '800',
    color: '#245f73',
    letterSpacing: 2,
  },
  logoPay: {
    color: '#733e24',
  },
  logoAdmin: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '400',
  },
  accountIconBtn: {
    position: 'absolute',
    right: spacing.md,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconBar: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.borderLight,
  },
  iconButton: {
    alignItems: 'center',
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    minWidth: 48,
    position: 'relative',
  },
  iconLabel: {
    fontSize: 11,
    color: c.textLight,
    marginTop: 2,
    fontWeight: '500',
  },
  iconLabelSmall: {
    fontSize: 10,
  },
  iconLabelActive: {
    color: c.primary,
    fontWeight: '700',
  },
  activeIndicator: {
    position: 'absolute',
    bottom: -spacing.sm,
    left: '20%',
    right: '20%',
    height: 3,
    backgroundColor: c.primary,
    borderRadius: 2,
  },
  // Desktop sidebar styles
  sidebar: {
    width: SIDEBAR_WIDTH,
    backgroundColor: c.surface,
    borderRightWidth: 1,
    borderRightColor: c.borderLight,
    height: '100%',
  },
  sidebarLogoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
  },
  sidebarLogoText: {
    fontSize: 22,
    fontWeight: '800',
    color: '#245f73',
    letterSpacing: 1,
  },
  sidebarScroll: {
    flex: 1,
  },
  sidebarScrollContent: {
    paddingBottom: spacing.xl,
  },
  sidebarItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    marginHorizontal: spacing.xs,
    borderRadius: borderRadius.sm,
    gap: spacing.sm,
    minHeight: 40,
  },
  sidebarItemActive: {
    backgroundColor: c.primaryBg,
  },
  sidebarLabel: {
    fontSize: fontSize.sm,
    color: c.textLight,
    fontWeight: fontWeight.medium,
    flex: 1,
  },
  sidebarLabelActive: {
    color: c.primary,
    fontWeight: fontWeight.semibold,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
